﻿// FOR TESTING ORCHESTRATOR V2 — TODO: DELETE

import React, { useEffect, useRef, useState } from "react";
import { Panel, IconButton, TooltipHost } from "@fluentui/react";
import { useGetToken } from "../../authConfig";
import { getHeaders } from "../../api/api";
import { usePowerPlant } from "../../components/powerplant";
import {
    configApiV1,
    PowerplantInfo,
    loadPowerplantConfigV2,
    getFilterOptionsApi,
    FilterOptionsResponse,
    ChatAppResponse,
    ResponseContext,
    Thoughts,
    FileAttachment,
    ErrorDTO
} from "../../api";
import { KnowledgeSource, FilterSettings } from "../../components/FilterSettings";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { ClearChatButton } from "../../components/ClearChatButton";
import { QuestionInput } from "../../components/QuestionInput";
import { UserChatMessage } from "../../components/UserChatMessage";
import { PowerplantModal } from "../../components/PowerplantModal";
import { Spinner } from "../../components/Spinner";
import styles from "../chat/Chat.module.css";

const BACKEND_URI = "";

const EMPTY_CONTEXT: ResponseContext = {
    data_points: [],
    followup_questions: null,
    thoughts: [],
    sharepoint_uris: {},
    powerplant: ""
};

function makeResponse(content: string, context: Partial<ResponseContext> = {}): ChatAppResponse {
    return {
        choices: [
            {
                index: 0,
                session_state: null,
                message: { content, role: "assistant" },
                context: { ...EMPTY_CONTEXT, ...context }
            }
        ]
    };
}

export default function OrchestratorV2Test() {
    const token = useGetToken();
    const { powerplants, setPowerplants, selectedPlantId, setSelectedPlantId } = usePowerPlant();

    // ── powerplant modal ─────────────────────────────────────────────────────
    const [showPowerplantModal, setShowPowerplantModal] = useState(false);
    const [configLoading, setConfigLoading] = useState(true);
    const [isPowerplantLoading, setIsPowerplantLoading] = useState(false);

    // ── filter / knowledge-source state (mirrors Chat.tsx) ──────────────────
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [filters, setFilters] = useState<{ name: string; displayName: string; values: { text: string; key: string }[] }[]>([]);
    const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});
    const [selectedKnowledgeSources, setSelectedKnowledgeSources] = useState<KnowledgeSource[]>([]);
    const [availableKnowledgeSources, setAvailableKnowledgeSources] = useState<KnowledgeSource[]>([]);
    const [availableValues, setAvailableValues] = useState<Record<string, Record<string, number>>>({});
    const [totalDocuments, setTotalDocuments] = useState<number | undefined>(undefined);
    const [isLoadingFilterOptions, setIsLoadingFilterOptions] = useState(false);

    const discardKeys = ["elisabot"];

    const getConfig = async () => {
        setConfigLoading(true);
        try {
            const config = await configApiV1(token);
            const normalizedPowerplants: Record<string, PowerplantInfo> = {};
            Object.entries(config.powerplants ?? {}).forEach(([key, value]) => {
                if (discardKeys.includes(key)) return;
                normalizedPowerplants[key] = typeof value === "string" ? { id: value } : (value as PowerplantInfo);
            });
            setPowerplants(normalizedPowerplants);
        } catch (err) {
            console.error("Config load failed", err);
        } finally {
            setConfigLoading(false);
        }
    };

    useEffect(() => {
        getConfig();
    }, []);
    useEffect(() => {
        setShowPowerplantModal(true);
    }, []);

    // Load plant config (filters + knowledge sources) whenever plant changes
    useEffect(() => {
        if (!selectedPlantId) return;
        const load = async () => {
            setIsPowerplantLoading(true);
            try {
                const cfg = await loadPowerplantConfigV2(selectedPlantId, token);
                setSelectedKnowledgeSources(cfg.knowledgeSources);
                setAvailableKnowledgeSources(cfg.knowledgeSources);
                setAvailableValues({});
                setTotalDocuments(undefined);
                const newFilters = cfg.uiFields ?? [];
                setFilters(newFilters);
                const clearedSelections: Record<string, string[]> = {};
                newFilters.forEach(f => {
                    clearedSelections[f.name] = [];
                });
                setSelectedFilters(clearedSelections);
            } catch (err) {
                console.error("Failed to load powerplant config", err);
            } finally {
                setIsPowerplantLoading(false);
                setShowPowerplantModal(false);
            }
        };
        load();
    }, [selectedPlantId]);

    // Dynamic filter options
    useEffect(() => {
        if (!selectedPlantId || filters.length === 0) return;
        const timer = setTimeout(async () => {
            setIsLoadingFilterOptions(true);
            try {
                const result: FilterOptionsResponse = await getFilterOptionsApi(selectedPlantId, selectedFilters, token);
                setAvailableValues(result.available_values);
                setTotalDocuments(result.total_documents);
            } catch (err) {
                console.error("Error fetching filter options:", err);
            } finally {
                setIsLoadingFilterOptions(false);
            }
        }, 150);
        return () => clearTimeout(timer);
    }, [selectedFilters, selectedPlantId, filters, token]);

    const handlePowerplantSelect = (plantId: string) => {
        setSelectedPlantId(plantId);
    };

    const activeFilterCount = Object.values(selectedFilters).filter(v => Array.isArray(v) && v.length > 0).length;

    // ── chat state ───────────────────────────────────────────────────────────
    const lastQuestionRef = useRef<string>("");
    const lastQuestionFilesRef = useRef<string[]>([]);
    const threadIdRef = useRef<string>(crypto.randomUUID());
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    const [answers, setAnswers] = useState<[string, ChatAppResponse][]>([]);
    const [answersFiles, setAnswersFiles] = useState<string[][]>([]);
    const [answersActivity, setAnswersActivity] = useState<
        { steps: { name: string; done: boolean }[]; toolCalls: { name: string; args: string; done: boolean }[] }[]
    >([]);
    const [streamedAnswer, setStreamedAnswer] = useState<[string, ChatAppResponse] | null>(null);
    const [steps, setSteps] = useState<{ name: string; done: boolean }[]>([]);
    const [toolCalls, setToolCalls] = useState<Record<string, { name: string; args: string; done: boolean; result?: unknown }>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState<unknown>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);
    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);

    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [isLoading, streamedAnswer]);

    const clearChat = () => {
        abortRef.current?.abort();
        lastQuestionRef.current = "";
        lastQuestionFilesRef.current = [];
        threadIdRef.current = crypto.randomUUID();
        setAnswers([]);
        setAnswersFiles([]);
        setAnswersActivity([]);
        setStreamedAnswer(null);
        setSteps([]);
        setToolCalls({});
        setIsLoading(false);
        setIsStreaming(false);
        setError(undefined);
        setActiveAnalysisPanelTab(undefined);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }
        setSelectedAnswer(index);
    };

    const makeApiRequest = async (question: string, files?: FileAttachment[]) => {
        if (!selectedPlantId) {
            setError(new Error("No powerplant selected."));
            return;
        }
        lastQuestionRef.current = question;
        lastQuestionFilesRef.current = files ? files.map(f => f.filename) : [];
        setError(undefined);
        setIsLoading(true);
        setIsStreaming(false);
        setStreamedAnswer(null);
        setSteps([]);
        setToolCalls({});

        abortRef.current = new AbortController();

        const history = answers.flatMap(([q, r]) => [
            { role: "user", content: q },
            { role: "assistant", content: r.choices[0].message.content }
        ]);

        try {
            const response = await fetch(`${BACKEND_URI}/api/v2/chat`, {
                method: "POST",
                headers: {
                    ...getHeaders(token),
                    "Content-Type": "application/json",
                    Accept: "text/event-stream"
                },
                body: JSON.stringify({
                    stream: true,
                    thread_id: threadIdRef.current,
                    messages: [...history, { role: "user", content: question, files: files && files.length > 0 ? files : undefined }],
                    context: {
                        callerApp: "alexandria_ui",
                        site: selectedPlantId ?? undefined,
                        overrides: {
                            filter_powerplant: selectedPlantId,
                            filters: selectedFilters
                        },
                        knowledge_source: selectedKnowledgeSources
                    },
                    session_state: answers.length ? answers[answers.length - 1][1].choices[0].session_state : null
                }),
                signal: abortRef.current.signal
            });

            if (!response.ok || !response.body) {
                const dto: Partial<ErrorDTO> | null = await response.json().catch(() => null);
                throw new Error(dto?.message ?? dto?.error ?? `HTTP ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buf = "";
            let accText = "";
            let followups: string[] = [];
            const toolResults: { name: string; result: { description: unknown; props?: Record<string, unknown> } }[] = [];
            let sharepoint_uris: Record<string, string> = {};
            const localSteps: { name: string; done: boolean }[] = [];
            const localToolCalls: Record<string, { name: string; args: string; done: boolean }> = {};

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const lines = buf.split("\n");
                buf = lines.pop() ?? "";

                for (const line of lines) {
                    if (!line.startsWith("data:")) continue;
                    let ev: Record<string, unknown>;
                    try {
                        ev = JSON.parse(line.slice(5).trim());
                    } catch {
                        continue;
                    }

                    switch (ev.type) {
                        case "TOOL_CALL_START": {
                            const tcName = ev.toolCallName as string;
                            const tcId = ev.toolCallId as string;
                            localToolCalls[tcId] = { name: tcName, args: "", done: false };
                            if (!steps.some(s => s.name === tcName)) setToolCalls(prev => ({ ...prev, [tcId]: { name: tcName, args: "", done: false } }));
                            break;
                        }
                        case "TOOL_CALL_ARGS": {
                            const tcId = ev.toolCallId as string;
                            if (localToolCalls[tcId]) localToolCalls[tcId].args += ev.delta as string;
                            setToolCalls(prev => {
                                const tc = prev[tcId];
                                if (!tc) return prev;
                                return { ...prev, [tcId]: { ...tc, args: tc.args + (ev.delta as string) } };
                            });
                            break;
                        }
                        case "TOOL_CALL_END": {
                            const tcId = ev.toolCallId as string;
                            if (localToolCalls[tcId]) localToolCalls[tcId].done = true;
                            setToolCalls(prev => {
                                const tc = prev[tcId];
                                if (!tc) return prev;
                                return { ...prev, [tcId]: { ...tc, done: true } };
                            });
                            break;
                        }
                        case "TOOL_CALL_RESULT": {
                            const tcId = ev.toolCallId as string;
                            setToolCalls(prev => {
                                const tc = prev[tcId];
                                if (!tc) return prev;
                                let parsed: unknown = ev.content;
                                try {
                                    parsed = JSON.parse(ev.content as string);
                                } catch {
                                    /* keep raw string */
                                }
                                // Normalize plain strings (e.g. framework errors) into the expected shape
                                if (typeof parsed !== "object" || parsed === null || !("description" in parsed)) {
                                    let displayText = String(parsed);
                                    if (tc.name === "load_skill") {
                                        try {
                                            const a = JSON.parse(tc.args);
                                            displayText = a.skill_name ?? displayText.slice(0, 100);
                                        } catch {
                                            displayText = displayText.slice(0, 100);
                                        }
                                    }
                                    parsed = { description: [displayText], props: {} };
                                }
                                toolResults.push({ name: tc.name, result: parsed as { description: unknown; props?: Record<string, unknown> } });
                                return { ...prev, [tcId]: { ...tc, result: parsed } };
                            });
                            break;
                        }
                        case "STEP_STARTED":
                            localSteps.push({ name: ev.stepName as string, done: false });
                            setSteps(prev => [...prev, { name: ev.stepName as string, done: false }]);
                            break;
                        case "STEP_FINISHED": {
                            const ls = localSteps.find(s => s.name === ev.stepName);
                            if (ls) ls.done = true;
                            setSteps(prev => prev.map(s => (s.name === ev.stepName ? { ...s, done: true } : s)));
                            break;
                        }
                        case "TEXT_MESSAGE_CONTENT":
                            if (!isStreaming) {
                                setIsLoading(false);
                                setIsStreaming(true);
                                setStreamedAnswer([question, makeResponse("")]);
                            }
                            accText += ev.delta as string;
                            setStreamedAnswer([question, makeResponse(accText)]);
                            break;
                        case "CUSTOM":
                            if (ev.name === "followup_questions") followups = ev.value as string[];
                            if (ev.name === "alexandria_context") {
                                const ctx = ev.value as { sharepoint_uris: Record<string, string> };
                                sharepoint_uris = ctx.sharepoint_uris ?? {};
                            }
                            break;
                        case "RUN_ERROR":
                            throw new Error(ev.message as string);
                    }
                }
            }

            const thoughts: Thoughts[] = toolResults.map(tr => ({
                title: tr.name,
                description: tr.result.description,
                props: tr.result.props as { [key: string]: string } | undefined
            }));
            const final = makeResponse(accText, { thoughts, sharepoint_uris, followup_questions: followups });
            setAnswers(prev => [...prev, [question, final]]);
            setAnswersFiles(prev => [...prev, lastQuestionFilesRef.current]);
            setAnswersActivity(prev => [...prev, { steps: localSteps, toolCalls: Object.values(localToolCalls) }]);
            setStreamedAnswer(null);
            setSteps([]);
        } catch (e: unknown) {
            if (e instanceof Error && e.name === "AbortError") return;
            setError(e);
        } finally {
            setIsLoading(false);
            setIsStreaming(false);
        }
    };

    const getToolCallLabel = (tc: { name: string; args: string }) => {
        let label = tc.name;
        try {
            const args = JSON.parse(tc.args);
            if (tc.name === "load_skill" && args.skill_name) {
                label += `: ${args.skill_name}`;
            } else {
                const first = Object.entries(args).find(([k]) => k !== "reasoning");
                if (first) label += `(${first[0]}: "${String(first[1]).slice(0, 40)}")`;
            }
        } catch {
            /* ignore */
        }
        return label;
    };

    return (
        <div className={styles.container}>
            {/* warning banner */}
            <div
                style={{
                    background: "#fff3cd",
                    borderBottom: "1px solid #ffc107",
                    padding: "6px 20px",
                    fontSize: 12,
                    color: "#856404",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexShrink: 0
                }}
            >
                FOR TESTING ORCHESTRATOR V2 — TODO: DELETE
            </div>

            <div className={styles.commandsContainer}>
                <ClearChatButton className={styles.commandButton} onClick={clearChat} disabled={!lastQuestionRef.current || isLoading} />
            </div>

            <div className={styles.chatRoot}>
                <div className={styles.chatContainer}>
                    {!lastQuestionRef.current ? (
                        <div className={styles.chatEmptyState}>
                            <h1 className={styles.chatEmptyStateTitle}>AlexandrIA V2 Test</h1>
                        </div>
                    ) : (
                        <div className={styles.chatMessageStream}>
                            {!isStreaming &&
                                answers.map(([q, r], i) => (
                                    <div key={i}>
                                        <UserChatMessage message={q} files={answersFiles[i]} />
                                        {answersActivity[i]?.steps.length > 0 && (
                                            <div style={{ padding: "8px 0 4px 16px", display: "flex", flexWrap: "wrap", gap: 6 }}>
                                                {answersActivity[i].steps.map((s, j) => (
                                                    <span
                                                        key={j}
                                                        style={{
                                                            fontSize: 12,
                                                            padding: "3px 10px",
                                                            borderRadius: 12,
                                                            background: "#e6f4ea",
                                                            color: "#2e7d32",
                                                            border: "1px solid #a5d6a7"
                                                        }}
                                                    >
                                                        ✓ {s.name}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {answersActivity[i]?.toolCalls.length > 0 && (
                                            <div style={{ padding: "2px 0 4px 16px", display: "flex", flexWrap: "wrap", gap: 4 }}>
                                                {answersActivity[i].toolCalls.map((tc, j) => (
                                                    <span
                                                        key={j}
                                                        style={{
                                                            fontFamily: "monospace",
                                                            fontSize: 11,
                                                            padding: "2px 8px",
                                                            borderRadius: 4,
                                                            background: "#f0f0f0",
                                                            color: "#555",
                                                            border: "1px solid #c7d7f9",
                                                            opacity: 0.7
                                                        }}
                                                    >
                                                        {getToolCallLabel(tc)}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <div className={styles.chatMessageGpt}>
                                            <Answer
                                                isStreaming={false}
                                                answer={r}
                                                isSelected={selectedAnswer === i && activeAnalysisPanelTab !== undefined}
                                                onCitationClicked={() => {}}
                                                onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, i)}
                                                onSupportingContentClicked={() => {}}
                                                onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                showFollowupQuestions={i === answers.length - 1}
                                            />
                                        </div>
                                    </div>
                                ))}

                            {isStreaming && streamedAnswer != null && (
                                <div>
                                    <UserChatMessage message={streamedAnswer[0]} files={lastQuestionFilesRef.current} />
                                    {steps.length > 0 && (
                                        <div style={{ padding: "8px 0 4px 16px", display: "flex", flexWrap: "wrap", gap: 6 }}>
                                            {steps.map((s, i) => (
                                                <span
                                                    key={i}
                                                    style={{
                                                        fontSize: 12,
                                                        padding: "3px 10px",
                                                        borderRadius: 12,
                                                        background: s.done ? "#e6f4ea" : "#fff8e1",
                                                        color: s.done ? "#2e7d32" : "#795548",
                                                        border: `1px solid ${s.done ? "#a5d6a7" : "#ffe082"}`
                                                    }}
                                                >
                                                    {s.done ? "✓" : "⏳"} {s.name}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {Object.values(toolCalls).length > 0 && (
                                        <div style={{ padding: "2px 0 4px 16px", display: "flex", flexWrap: "wrap", gap: 4 }}>
                                            {Object.values(toolCalls).map((tc, i) => {
                                                const label = getToolCallLabel(tc);
                                                return (
                                                    <span
                                                        key={i}
                                                        style={{
                                                            fontFamily: "monospace",
                                                            fontSize: 11,
                                                            padding: "2px 8px",
                                                            borderRadius: 4,
                                                            background: tc.done ? "#f0f0f0" : "#e8f0fe",
                                                            color: tc.done ? "#555" : "#1a56db",
                                                            border: "1px solid #c7d7f9",
                                                            opacity: tc.done ? 0.7 : 1
                                                        }}
                                                    >
                                                        {label}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <div className={styles.chatMessageGpt}>
                                        <Answer
                                            isStreaming={true}
                                            answer={streamedAnswer[1]}
                                            isSelected={false}
                                            onCitationClicked={() => {}}
                                            onThoughtProcessClicked={() => {}}
                                            onSupportingContentClicked={() => {}}
                                            onFollowupQuestionClicked={() => {}}
                                            showFollowupQuestions={false}
                                        />
                                    </div>
                                </div>
                            )}

                            {isLoading && !isStreaming && (
                                <>
                                    <UserChatMessage message={lastQuestionRef.current} files={lastQuestionFilesRef.current} />
                                    {steps.length > 0 && (
                                        <div style={{ padding: "8px 0 4px 16px", display: "flex", flexWrap: "wrap", gap: 6 }}>
                                            {steps.map((s, i) => (
                                                <span
                                                    key={i}
                                                    style={{
                                                        fontSize: 12,
                                                        padding: "3px 10px",
                                                        borderRadius: 12,
                                                        background: s.done ? "#e6f4ea" : "#fff8e1",
                                                        color: s.done ? "#2e7d32" : "#795548",
                                                        border: `1px solid ${s.done ? "#a5d6a7" : "#ffe082"}`
                                                    }}
                                                >
                                                    {s.done ? "✓" : "⏳"} {s.name}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {Object.values(toolCalls).length > 0 && (
                                        <div style={{ padding: "2px 0 4px 16px", display: "flex", flexWrap: "wrap", gap: 4 }}>
                                            {Object.values(toolCalls).map((tc, i) => {
                                                const label = getToolCallLabel(tc);
                                                return (
                                                    <span
                                                        key={i}
                                                        style={{
                                                            fontFamily: "monospace",
                                                            fontSize: 11,
                                                            padding: "2px 8px",
                                                            borderRadius: 4,
                                                            background: tc.done ? "#f0f0f0" : "#e8f0fe",
                                                            color: tc.done ? "#555" : "#1a56db",
                                                            border: "1px solid #c7d7f9",
                                                            opacity: tc.done ? 0.7 : 1
                                                        }}
                                                    >
                                                        {label}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerLoading />
                                    </div>
                                </>
                            )}

                            {error != null && (
                                <>
                                    <UserChatMessage message={lastQuestionRef.current} files={lastQuestionFilesRef.current} />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerError error={String(error)} onRetry={() => makeApiRequest(lastQuestionRef.current)} />
                                    </div>
                                </>
                            )}
                            <div ref={chatMessageStreamEnd} />
                        </div>
                    )}

                    <div className={styles.chatInput}>
                        <QuestionInput
                            clearOnSend
                            placeholder="Type a new question"
                            disabled={isLoading || !selectedPlantId}
                            onSend={(question, files) => makeApiRequest(question, files)}
                            onFilterToggle={() => setIsConfigPanelOpen(prev => !prev)}
                            hasActiveFilters={activeFilterCount > 0}
                            powerplantId={selectedPlantId || undefined}
                            token={token}
                        />
                    </div>
                </div>

                <Panel
                    isOpen={isConfigPanelOpen}
                    isBlocking={false}
                    onDismiss={() => setIsConfigPanelOpen(false)}
                    closeButtonAriaLabel="Close"
                    hasCloseButton={false}
                    onRenderNavigation={() => (
                        <div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 14px 12px 24px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <span style={{ fontFamily: "monospace", fontWeight: 600, fontSize: "20px", color: "rgb(50, 49, 48)" }}>Knowledge base</span>
                                    <TooltipHost content="Select which knowledge base to use" calloutProps={{ gapSpace: 4 }}>
                                        <IconButton
                                            iconProps={{ iconName: "Info" }}
                                            styles={{
                                                root: { width: 28, height: 28, color: "rgb(160, 158, 156)" },
                                                rootHovered: { color: "rgb(96, 94, 92)", background: "transparent" },
                                                icon: { fontSize: 16, color: "inherit" }
                                            }}
                                        />
                                    </TooltipHost>
                                </div>
                                <IconButton
                                    iconProps={{ iconName: "Cancel" }}
                                    ariaLabel="Close"
                                    onClick={() => setIsConfigPanelOpen(false)}
                                    styles={{
                                        root: { color: "rgb(96, 94, 92)" },
                                        rootHovered: { color: "rgb(50, 49, 48)", background: "rgb(243, 242, 241)" }
                                    }}
                                />
                            </div>
                            <div style={{ borderBottom: "1px solid rgb(237, 235, 233)", margin: "0 24px" }} />
                        </div>
                    )}
                >
                    {isPowerplantLoading ? (
                        <Spinner label="Loading filters..." />
                    ) : (
                        <FilterSettings
                            filters={filters}
                            selectedFilters={selectedFilters}
                            updateFilters={setSelectedFilters}
                            selectedKnowledgeSources={selectedKnowledgeSources}
                            knowledgeSources={availableKnowledgeSources}
                            onKnowledgeSourcesChange={setSelectedKnowledgeSources}
                            onClearFilters={() => {
                                const cleared: Record<string, string[]> = {};
                                filters.forEach(f => {
                                    cleared[f.name] = [];
                                });
                                setSelectedFilters(cleared);
                            }}
                            availableValues={availableValues}
                            totalDocuments={totalDocuments}
                            isLoadingFilterOptions={isLoadingFilterOptions}
                        />
                    )}
                </Panel>

                {answers.length > 0 && activeAnalysisPanelTab && (
                    <AnalysisPanel
                        className={styles.chatAnalysisPanel}
                        activeTab={activeAnalysisPanelTab}
                        onActiveTabChanged={tab => onToggleTab(tab, selectedAnswer)}
                        answer={answers[selectedAnswer][1]}
                        activeCitation={undefined}
                        citationHeight="810px"
                    />
                )}
            </div>

            <PowerplantModal
                showPowerplantModal={showPowerplantModal}
                configLoading={configLoading}
                selectedPlantId={selectedPlantId}
                powerplants={powerplants}
                handlePowerplantSelect={handlePowerplantSelect}
                isPowerplantLoading={isPowerplantLoading}
            />
        </div>
    );
}
